/**
 * Dimensions
 * @author David <david.prenninger@gmx.de>
 *
 * This file provides all helper functions related counter dimensions and area point checks.
 */

import * as THREE from 'three';
import { degreeToMatrix, calculateIntersection } from '@/helpers/visualization/geometry';
import { checkForWarning } from './visualization/Warning';
import {
  TABLE_PLAIN,
  TABLE_CORNER,
  TABLE_UCORNER,
} from '../constants';

const classifyPoint = require('point-in-polygon');

const generalDimension = (
  x,
  y,
  z,
  radius = 0,
  raisedHeight = undefined,
  raisedWidth = undefined,
) => ({
  x,
  y,
  z,
  raisedHeight,
  raisedWidth,
  child: null,
  childAngle: 0,
  childRadius: radius,
});

export const pointInAreas = (point, areas) => {
  let pointLivesInArea = false;
  areas.forEach((area) => {
    const polygon = area.map((vertex) => [vertex.x, vertex.z]);
    if (classifyPoint([point.clone().x, point.clone().z], polygon)) {
      pointLivesInArea = true;
    }
  });
  return pointLivesInArea;
};


export const calculateAreas = (sides) => {
  const vertices = [];

  // FRONT
  if (sides[0].raisedWidth) {
    vertices.push(
      new THREE.Vector3(sides[0].raisedWidth, 0, 0),
      new THREE.Vector3(0, 0, 0),
    );
  } else {
    vertices.push(
      new THREE.Vector3(sides[0].x, 0, 0),
      new THREE.Vector3(0, 0, 0),
    );
  }

  const frontLineDirectionMatrix = degreeToMatrix(0);
  let frontLineOrigin = new THREE.Vector3(0, 0, 0);

  let backLineOrigin;
  if (sides[0].raisedWidth) {
    backLineOrigin = new THREE.Vector3(sides[0].raisedWidth, 0, 0);
  } else {
    backLineOrigin = new THREE.Vector3(sides[0].x, 0, 0);
  }
  let backLineDirectionMatrix = degreeToMatrix(0);

  sides.forEach((side, index) => {
    const frontLineVector = new THREE.Vector3(0, 0, side.y);
    frontLineVector.applyMatrix4(frontLineDirectionMatrix);
    frontLineVector.add(frontLineOrigin);

    frontLineDirectionMatrix.multiply(degreeToMatrix(side.childAngle));
    const sideDirectionMatrix = frontLineDirectionMatrix.clone().multiply(degreeToMatrix(90));
    frontLineOrigin = frontLineVector;
    let backlineIntersect = null;

    if (index < sides.length - 1) {
      const bSideOrigin = new THREE.Vector3(0, 0, 1);
      bSideOrigin.applyMatrix4(sideDirectionMatrix);
      if (sides[index + 1].raisedWidth) {
        bSideOrigin.multiplyScalar(sides[index + 1].raisedWidth);
      } else {
        bSideOrigin.multiplyScalar(sides[index + 1].x);
      }

      bSideOrigin.add(frontLineOrigin);

      const frontSideVector = new THREE.Vector3(0, 0, 1);
      frontSideVector.applyMatrix4(frontLineDirectionMatrix);

      const backlineVector = new THREE.Vector3(0, 0, 1);
      backlineVector.applyMatrix4(backLineDirectionMatrix);

      backlineIntersect = calculateIntersection(
        bSideOrigin,
        frontSideVector,
        backLineOrigin,
        backlineVector,
      );
    } else {
      const bSideOrigin = new THREE.Vector3(0, 0, 1);
      bSideOrigin.applyMatrix4(sideDirectionMatrix);
      if (side.raisedWidth) {
        bSideOrigin.multiplyScalar(side.raisedWidth);
      } else {
        bSideOrigin.multiplyScalar(side.x);
      }

      bSideOrigin.add(frontLineOrigin);
      backlineIntersect = bSideOrigin;
    }

    backLineOrigin = backlineIntersect;
    backLineDirectionMatrix = frontLineDirectionMatrix.clone();

    vertices.push(
      backlineIntersect,
      frontLineVector,
    );
  });

  const areas = [];
  const areaCount = (vertices.length - 2);
  for (let i = 0; i < areaCount; i += 2) {
    areas.push([
      vertices[i + 1].clone(),
      vertices[i].clone(),
      vertices[i + 2].clone(),
      vertices[i + 3].clone(),
    ]);
  }

  return areas;
};


export const generateDimensions = (table) => {
  if (table.id === TABLE_PLAIN) {
    const dimension = [];
    if (table.ledge) {
      dimension.push(generalDimension(2, 5, 2, 0, 0.3, 0.84));
    } else {
      dimension.push(generalDimension(2, 5, 2));
    }
    return dimension;
  }
  if (table.id === TABLE_CORNER) {
    const dimension = [];
    if (table.ledge) {
      dimension.push(generalDimension(2, 5, 2, 0, 0.3, 0.84));
      dimension[0].childAngle = 90;
      dimension.push(generalDimension(2, 5, 2, 0, 0.3, 0.84));
    } else {
      dimension.push(generalDimension(2, 5, 2));
      dimension[0].childAngle = 90;
      dimension.push(generalDimension(2, 5, 2));
    }
    return dimension;
  }
  if (table.id === TABLE_UCORNER) {
    const dimension = [];
    if (table.ledge) {
      dimension.push(generalDimension(1.5, 5, 2, 0, 0.3, 0.84));
      dimension[0].childAngle = 90;
      dimension.push(generalDimension(1.5, 6, 2, 0, 0.3, 0.84));
      dimension[1].childAngle = 90;
      dimension.push(generalDimension(1.5, 5, 2, 0, 0.3, 0.84));
    } else {
      dimension.push(generalDimension(1.5, 5, 2));
      dimension[0].childAngle = 90;
      dimension.push(generalDimension(1.5, 6, 2));
      dimension[1].childAngle = 90;
      dimension.push(generalDimension(1.5, 5, 2));
    }
    return dimension;
  }
  return null;
};

const calculateLinePoint = (objects, offset) => {
  const linePoint = new THREE.Vector3(offset.x, 0, offset.y);
  const lineDirectionMatrix = degreeToMatrix(0);
  objects.forEach((object) => {
    if (object.connector) {
      if (object.connector.size > 0) {
        if (object.connector.angle === 90 || object.connector.angle === 45) {
          let objectSize = new THREE.Vector3(0, 0, object.connector.size / 1000);
          if (!object.stand) {
            objectSize = objectSize.clone()
              .applyMatrix4(lineDirectionMatrix)
              .add(objectSize.clone().applyMatrix4(
                lineDirectionMatrix.clone().multiply(degreeToMatrix(object.connector.angle)),
              ));
          } else {
            objectSize = objectSize.applyMatrix4(lineDirectionMatrix);
          }
          linePoint.add(objectSize);
        } else {
          const connectorVector = new THREE.Vector3(0, 0, object.connector.size / 500);
          connectorVector.applyMatrix4(lineDirectionMatrix);
          linePoint.add(connectorVector);
        }
      }
      lineDirectionMatrix.multiply(degreeToMatrix(object.connector.angle));
    }
    if (object.glass) {
      const glassVector = new THREE.Vector3(0, 0, object.glass.width);
      glassVector.applyMatrix4(lineDirectionMatrix);
      linePoint.add(glassVector);
    }
  });
  return linePoint;
};

export const checkIfLastObjectPointInArea = (objects, dimensions, offset) => {
  const areas = calculateAreas(dimensions);
  const linePoint = calculateLinePoint(objects, offset);
  return pointInAreas(linePoint, areas);
};

const calculateClampDistanceFromSides = (point, area) => {
  const distanceFront = point.clone().sub(calculateIntersection(
    area[0],
    area[3].clone().sub(area[0]).normalize(),
    point,
    area[3].clone().sub(area[0]).normalize().applyMatrix4(degreeToMatrix(-90)),
  )).length();
  const distanceBack = point.clone().sub(calculateIntersection(
    area[1],
    area[1].clone().sub(area[2]).normalize(),
    point,
    area[1].clone().sub(area[2]).normalize().applyMatrix4(degreeToMatrix(90)),
  )).length();
  return { distanceFront, distanceBack };
};


const calculateClampAreas = (areas, minClamp, maxClamp, pointToCheck) => {
  let outValue = false;
  areas.forEach((area) => {
    const areaMaxPoints = [];
    const areaMinPoints = [];
    const areaNormal = new THREE.Vector3(area[3].x, area[3].y, area[3].z)
      .sub(new THREE.Vector3(area[0].x, area[0].y, area[0].z)).normalize();
    area.forEach((point, index) => {
      const origin = new THREE.Vector3(point.x, point.y, point.z);
      if (index === 1 || index === 3) {
        areaNormal.multiplyScalar(-1);
      }
      areaMaxPoints.push(
        origin.clone().add(
          areaNormal.applyMatrix4(degreeToMatrix(90)).clone().multiplyScalar(maxClamp),
        ),
      );
      areaMinPoints.push(
        origin.clone().add(areaNormal.clone().multiplyScalar(minClamp)),
      );
      areaNormal.applyMatrix4(degreeToMatrix(-90));
    });

    const areaLeft = [
      areaMinPoints[0],
      areaMaxPoints[0],
      areaMinPoints[2],
      areaMaxPoints[2],
    ];
    const areaRight = [
      areaMinPoints[1],
      areaMaxPoints[1],
      areaMinPoints[3],
      areaMaxPoints[3],
    ];

    if (pointInAreas(pointToCheck, [areaLeft]) && pointInAreas(pointToCheck, [areaRight])) {
      outValue = calculateClampDistanceFromSides(pointToCheck, area);
    }
  });
  return outValue;
};

const checkPointClampApproved = (areas, linePoint) => {
  const areaClampable = calculateClampAreas(areas, 150 / 1000, 420 / 1000, linePoint);
  if (areaClampable) {
    return areaClampable;
  }
  return false;
};


export const checkIfPointIsClampApproved = (objects, dimensions, offset, vue) => {
  const areas = calculateAreas(dimensions);
  const linePoint = new THREE.Vector3(offset.x, 0, offset.y);
  const lineDirectionMatrix = degreeToMatrix(0);
  objects.forEach((object, index) => {
    if (index > 0) {
      const clampOneApproval = checkPointClampApproved(areas, linePoint);
      vue.$store.dispatch('setClampApproval', { index, data: { first: clampOneApproval } });
    }

    if (object.connector) {
      if (object.connector.size > 0) {
        if (object.connector.angle === 90 || object.connector.angle === 45) {
          let objectSize = new THREE.Vector3(0, 0, object.connector.size / 1000);
          if (!object.stand) {
            objectSize = objectSize.clone()
              .applyMatrix4(lineDirectionMatrix)
              .add(objectSize.clone().applyMatrix4(
                lineDirectionMatrix.clone().multiply(degreeToMatrix(object.connector.angle)),
              ));
          } else {
            objectSize = objectSize.applyMatrix4(lineDirectionMatrix);
          }
          linePoint.add(objectSize);
        } else {
          const connectorVector = new THREE.Vector3(0, 0, object.connector.size / 500);
          connectorVector.applyMatrix4(lineDirectionMatrix);
          linePoint.add(connectorVector);
        }
      }
      lineDirectionMatrix.multiply(degreeToMatrix(object.connector.angle));
    }

    if (object.glass) {
      const clampTwoApproval = checkPointClampApproved(areas, linePoint);
      vue.$store.dispatch('setClampApproval', { index, data: { second: clampTwoApproval } });
      const glassVector = new THREE.Vector3(0, 0, object.glass.width);
      glassVector.applyMatrix4(lineDirectionMatrix);
      linePoint.add(glassVector);
    }
  });

  checkForWarning(
    objects,
    vue.$store.getters.clampApproval,
    vue.$store.getters.standApproval,
    vue,
  );
};


export const checkForStandApproval = (objects, dimensions, offset, vue) => {
  const areas = calculateAreas(dimensions);

  const linePoint = new THREE.Vector3(offset.x, 0, offset.y);
  const lineDirectionMatrix = degreeToMatrix(0);
  if (objects.length === 0) {
    vue.$store.dispatch(
      'setStandApproval',
      { index: 0, data: { stand: calculateClampDistanceFromSides(linePoint, areas[0]) } },
    );
  }
  objects.forEach((object, index) => {
    areas.forEach((area) => {
      if (pointInAreas(linePoint, [area])) {
        vue.$store.dispatch(
          'setStandApproval',
          { index, data: { stand: calculateClampDistanceFromSides(linePoint, area) } },
        );
      }
    });

    if (object.connector) {
      if (object.connector.size > 0) {
        if (object.connector.angle === 90 || object.connector.angle === 45) {
          let objectSize = new THREE.Vector3(0, 0, object.connector.size / 1000);
          if (!object.stand) {
            objectSize = objectSize.clone()
              .applyMatrix4(lineDirectionMatrix)
              .add(objectSize.clone().applyMatrix4(
                lineDirectionMatrix.clone().multiply(degreeToMatrix(object.connector.angle)),
              ));
          } else {
            objectSize = objectSize.applyMatrix4(lineDirectionMatrix);
          }
          linePoint.add(objectSize);
        } else {
          const connectorVector = new THREE.Vector3(0, 0, object.connector.size / 500);
          connectorVector.applyMatrix4(lineDirectionMatrix);
          linePoint.add(connectorVector);
        }
      }
      lineDirectionMatrix.multiply(degreeToMatrix(object.connector.angle));
    }

    if (object.glass) {
      const glassVector = new THREE.Vector3(0, 0, object.glass.width);
      glassVector.applyMatrix4(lineDirectionMatrix);
      linePoint.add(glassVector);
    }
  });

  checkForWarning(
    objects,
    vue.$store.getters.clampApproval,
    vue.$store.getters.standApproval,
    vue,
  );
};
